/**
 * PatientInfoSummary — read-only metadata strip above the Personal Details
 * form. Mirrors Flutter `patientInfo()` at `create_patient_screen.dart:1023-1112`.
 *
 * Flutter renders this as an inline wrapped row of `LABEL: value` pairs on the
 * page background (NOT a bordered card), in BOTH create and edit modes — in
 * create mode every value is "N/A". Each pair is a bold uppercase label
 * (12px, muted) followed by the value (13px, regular):
 *
 *   PATIENT MRN: N/A   DOB: N/A   LANGUAGE: N/A   LAST UPDATE: N/A   STATUS: N/A
 */

import type { Patient } from '@/types/patient-api';

export interface PatientInfoSummaryProps {
  patient: Patient | null;
}

export function PatientInfoSummary({ patient }: PatientInfoSummaryProps) {
  const dob = formatDob(patient?.birthDate ?? null, patient?.age ?? null);
  const language = patient?.language ? patient.languageName?.name?.trim() || 'N/A' : 'N/A';
  const lastUpdate = formatLastUpdate(patient?.lastUpdated ?? null);

  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border-default bg-grey-100/40 px-6 py-3">
      <InfoPair label="Patient MRN" value={patient?.patientMrn ?? 'N/A'} />
      <InfoPair label="DOB" value={dob} />
      <InfoPair label="Language" value={language} truncate />
      <InfoPair label="Last update" value={lastUpdate} />
      <InfoPair label="Status" value={statusLabel(patient)} />
    </div>
  );
}

function InfoPair({
  label,
  value,
  truncate = false,
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <span className="text-xs font-bold uppercase tracking-wide text-text-muted">
        {label}:
      </span>
      <span
        className={
          truncate
            ? 'max-w-30 truncate text-size-sm text-text-primary'
            : 'text-size-sm text-text-primary'
        }
        title={truncate ? value : undefined}
      >
        {value}
      </span>
    </div>
  );
}

/** Flutter `:1102` — Merged > (isActive null → N/A) > Active / Inactive. */
function statusLabel(patient: Patient | null): string {
  if (patient?.isMerged) return 'Merged';
  if (patient?.isActive === null || patient?.isActive === undefined) return 'N/A';
  return patient.isActive ? 'Active' : 'Inactive';
}

function formatDob(iso: string | null, age: string | null): string {
  if (!iso) return 'N/A';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'N/A';
  const d = new Date(t);
  const formatted = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return age ? `${formatted} | ${age}` : formatted;
}

function formatLastUpdate(iso: string | null): string {
  if (!iso) return 'N/A';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'N/A';
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
